// src/hooks/useAddressManager.js
// ============================================
// CENTRALIZED ADDRESS MANAGEMENT HOOK
// ============================================
// Features:
// - Fetch, Create, Update, Delete addresses
// - Set default address
// - Auto-select default on load
// - Loading and error states
// - Cache management
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import addressService from '../AddressService/addressService';

const useAddressManager = (onAddressChange = null, userId = null) => {
  // State
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operationInProgress, setOperationInProgress] = useState(false);

  // Refs for preventing duplicate calls
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const selectedAddressRef = useRef(null); // ✅ Ref to break dependency cycle
  const FETCH_DEBOUNCE_MS = 500; // Prevent rapid refetches

  // ✅ Keep ref in sync with state
  useEffect(() => {
    selectedAddressRef.current = selectedAddress;
  }, [selectedAddress]);

  // ============================================
  // FETCH ADDRESSES
  // ============================================
  const fetchAddresses = useCallback(async (forceRefresh = false) => {
    // Debounce: Prevent rapid successive calls
    const now = Date.now();
    if (!forceRefresh && (now - lastFetchTimeRef.current) < FETCH_DEBOUNCE_MS) {
      console.log('⏭️ Skipping fetch - too soon since last fetch');
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      console.log('⏭️ Skipping fetch - already in progress');
      return;
    }

    try {
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;

      // ✅ FIX: Don't call address endpoint if user is not logged in
      if (!userId) {
        console.log('👤 No user logged in, skipping address fetch');
        setAddresses([]);
        setSelectedAddress(null);
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      setLoading(true);
      setError(null);

      console.log('📡 Fetching addresses...');
      const addressList = await addressService.getAddresses();

      // Handle empty or invalid response
      if (!addressList || !Array.isArray(addressList)) {
        console.warn('⚠️ Invalid address list received');
        setAddresses([]);
        setSelectedAddress(null);
        return;
      }

      console.log('✅ Addresses loaded:', addressList.length);
      setAddresses(addressList);

      // ✅ Use ref instead of state to avoid dependency cycle
      const currentSelected = selectedAddressRef.current;

      // Auto-select default address if not already selected
      if (!currentSelected || forceRefresh) {
        const defaultAddr = addressList.find(addr => addr.is_default) || addressList[0];

        if (defaultAddr) {
          console.log('🏠 Auto-selecting default address:', defaultAddr.label);
          setSelectedAddress(defaultAddr);

          // Notify parent component
          if (onAddressChange) {
            onAddressChange(defaultAddr);
          }
        }
      } else {
        // Update selected address if it was modified
        const updatedSelected = addressList.find(addr => addr.id === currentSelected.id);
        if (updatedSelected && JSON.stringify(updatedSelected) !== JSON.stringify(currentSelected)) {
          console.log('🔄 Updating selected address with latest data');
          setSelectedAddress(updatedSelected);

          if (onAddressChange) {
            onAddressChange(updatedSelected);
          }
        }
      }

    } catch (err) {
      console.error('❌ Failed to fetch addresses:', err);
      setError(err.message || 'Failed to load addresses');
      setAddresses([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [onAddressChange, userId]); // ✅ REMOVED selectedAddress - uses ref instead

  // ============================================
  // CREATE ADDRESS
  // ============================================
  const createAddress = useCallback(async (addressData) => {
    try {
      setOperationInProgress(true);
      setError(null);

      console.log('➕ Creating address:', addressData.label);

      // Validate required fields
      const requiredFields = ['label', 'address', 'city', 'state', 'zip_code'];
      const missingFields = requiredFields.filter(field => !addressData[field]);

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Validate phone (if provided)
      if (addressData.phone && addressData.phone.length !== 10) {
        throw new Error('Phone number must be 10 digits');
      }

      // Validate zip code
      if (addressData.zip_code.length !== 6) {
        throw new Error('ZIP code must be 6 digits');
      }

      // Determine if this should be default (first address)
      const isFirstAddress = addresses.length === 0;

      const payload = {
        label: addressData.label.trim(),
        address: addressData.address.trim(),
        apartment: addressData.apartment?.trim() || '',
        city: addressData.city.trim(),
        state: addressData.state.trim(),
        zip_code: addressData.zip_code.trim(),
        phone: addressData.phone?.trim() || '',
        recipient_name: addressData.recipient_name?.trim() || '',
        country: addressData.country || 'India',
        // No fabricated fallback — an address with no real GPS/map pin should
        // save as "location unknown" (null), not a fake coordinate that looks
        // real but silently breaks anything computed from it (e.g. delivery fee).
        latitude: addressData.latitude || null,
        longitude: addressData.longitude || null,
        is_default: isFirstAddress // First address is always default
      };

      const createdAddress = await addressService.createAddress(payload);

      console.log('✅ Address created:', createdAddress.id);

      // Refresh addresses list
      await fetchAddresses(true);

      // ✅ NOTIFY: Let other parts of the app know to re-fetch
      window.dispatchEvent(new Event('global_address_sync'));

      return { success: true, address: createdAddress };

    } catch (err) {
      console.error('❌ Failed to create address:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setOperationInProgress(false);
    }
  }, [addresses.length, fetchAddresses]);

  // ============================================
  // UPDATE ADDRESS
  // ============================================
  const updateAddress = useCallback(async (addressId, addressData) => {
    try {
      setOperationInProgress(true);
      setError(null);

      console.log('✏️ Updating address:', addressId);

      // Find existing address
      const existingAddress = addresses.find(addr => addr.id === addressId);
      if (!existingAddress) {
        throw new Error('Address not found');
      }

      // Validate required fields
      const requiredFields = ['label', 'address', 'city', 'state', 'zip_code'];
      const missingFields = requiredFields.filter(field => !addressData[field]);

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Validate zip code
      if (addressData.zip_code.length !== 6) {
        throw new Error('ZIP code must be 6 digits');
      }

      const payload = {
        label: addressData.label.trim(),
        address: addressData.address.trim(),
        apartment: addressData.apartment?.trim() || '',
        city: addressData.city.trim(),
        state: addressData.state.trim(),
        zip_code: addressData.zip_code.trim(),
        phone: addressData.phone?.trim() || '',
        recipient_name: addressData.recipient_name?.trim() || '',
        country: addressData.country || 'India',
        // No fabricated fallback (see createAddress) — fall back to the
        // existing real value if the edit didn't change location, otherwise null.
        latitude: addressData.latitude || existingAddress.latitude || null,
        longitude: addressData.longitude || existingAddress.longitude || null,
        is_default: addressData.is_default !== undefined ? addressData.is_default : existingAddress.is_default
      };

      const updatedAddress = await addressService.updateAddress(addressId, payload);

      console.log('✅ Address updated:', addressId);

      // Refresh addresses list
      await fetchAddresses(true);

      // ✅ NOTIFY: Let other parts of the app know to re-fetch
      window.dispatchEvent(new Event('global_address_sync'));

      return { success: true, address: updatedAddress };

    } catch (err) {
      console.error('❌ Failed to update address:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setOperationInProgress(false);
    }
  }, [addresses, fetchAddresses]);

  // ============================================
  // DELETE ADDRESS
  // ============================================
  const deleteAddress = useCallback(async (addressId) => {
    try {
      setOperationInProgress(true);
      setError(null);

      // Prevent deleting last address
      if (addresses.length <= 1) {
        throw new Error('Cannot delete the last address. You must have at least one address.');
      }

      console.log('🗑️ Deleting address:', addressId);

      await addressService.deleteAddress(addressId);

      console.log('✅ Address deleted');

      // If deleted address was selected, select another
      if (selectedAddress?.id === addressId) {
        const remainingAddresses = addresses.filter(addr => addr.id !== addressId);
        const newDefault = remainingAddresses.find(addr => addr.is_default) || remainingAddresses[0];

        if (newDefault) {
          setSelectedAddress(newDefault);

          if (onAddressChange) {
            onAddressChange(newDefault);
          }
        }
      }

      // Refresh addresses list
      await fetchAddresses(true);

      // ✅ NOTIFY: Let other parts of the app know to re-fetch
      window.dispatchEvent(new Event('global_address_sync'));

      return { success: true };

    } catch (err) {
      console.error('❌ Failed to delete address:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setOperationInProgress(false);
    }
  }, [addresses, selectedAddress, onAddressChange, fetchAddresses]);

  // ============================================
  // SET DEFAULT ADDRESS
  // ============================================
  const setDefaultAddress = useCallback(async (addressId) => {
    try {
      setOperationInProgress(true);
      setError(null);

      console.log('⭐ Setting default address:', addressId);

      // Find the address to update
      const addressToUpdate = addresses.find(addr => addr.id === addressId);
      if (!addressToUpdate) {
        throw new Error('Address not found');
      }

      // Update via API (sets is_default: true)
      await addressService.updateAddress(addressId, {
        ...addressToUpdate,
        is_default: true
      });

      console.log('✅ Default address updated');

      // Update local state immediately for better UX
      setAddresses(prevAddresses =>
        prevAddresses.map(addr => ({
          ...addr,
          is_default: addr.id === addressId
        }))
      );

      // Update selected address
      setSelectedAddress(addressToUpdate);

      if (onAddressChange) {
        onAddressChange(addressToUpdate);
      }

      // Refresh to get server state
      await fetchAddresses(true);

      // ✅ NOTIFY: Let other parts of the app know to re-fetch
      window.dispatchEvent(new Event('global_address_sync'));

      return { success: true };

    } catch (err) {
      console.error('❌ Failed to set default address:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setOperationInProgress(false);
    }
  }, [addresses, onAddressChange, fetchAddresses]);

  // ============================================
  // SELECT ADDRESS (UI only, no API call)
  // ============================================
  const selectAddress = useCallback((address) => {
    console.log('✅ Address selected:', address?.label || 'None');
    setSelectedAddress(address);

    if (onAddressChange) {
      onAddressChange(address);
    }
  }, [onAddressChange]);

  // ============================================
  // INITIAL LOAD & GLOBAL SYNC
  // ============================================
  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses, userId]); // Run when userId changes (login/logout)

  useEffect(() => {
    const handleGlobalSync = () => {
      console.log('🔄 Global address sync triggered, refreshing local hook state...');
      fetchAddresses(true);
    };

    window.addEventListener('global_address_sync', handleGlobalSync);
    return () => window.removeEventListener('global_address_sync', handleGlobalSync);
  }, [fetchAddresses]);

  // ============================================
  // RETURN HOOK API
  // ============================================
  return {
    // State
    addresses,
    selectedAddress,
    loading,
    error,
    operationInProgress,

    // Methods
    fetchAddresses,
    createAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    selectAddress,

    // Computed
    hasAddresses: addresses.length > 0,
    defaultAddress: addresses.find(addr => addr.is_default) || addresses[0]
  };
};

export default useAddressManager;